import React from 'react';

import { styled } from 'styled-components';
import { Box, Checkbox, Divider, Popover, TextField, InputAdornment, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import API from 'utils/api/api';
import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';
import { isNearBottom } from 'utils/scroll';
import { Licenses, SearchRequest, UuidableName } from 'utils/api/types';

import InitialAvatar from 'components/Avatar/InitialAvatar';
import HighlightedText from 'components/HighlightedText/HighlightedText';

import FilterButton from '../FilterButton';

export type AssignedToFilterValue = UuidableName;

const OptionsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  min-width: 250px;
  max-height: 350px;
`;

const SearchContainer = styled(Box)`
  padding: 8px;
`;

const OptionsListContainer = styled(Box)`
  overflow-y: auto;
  max-height: 250px;
`;

const OptionRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const UserInfo = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const UserTextColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const UserName = styled.span`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
`;

const UserEmail = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AllOptionText = styled.span`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  margin-left: 4px;
`;

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  padding: 16px;
`;

const NoResultsText = styled.span`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  padding: 16px;
  text-align: center;
  display: block;
`;

const AvatarsContainer = styled.div`
  display: flex;
  align-items: center;

  & > *:not(:first-child) {
    margin-left: -4px;
  }
`;

const CountAvatar = styled(InitialAvatar)`
  background-color: rgba(0, 0, 0, 0.08);

  & > * {
    color: rgba(0, 0, 0, 0.6);
    font-size: 12px;
    font-weight: 500;
  }
`;

const AssignedToPicker = ({
  value,
  onChange,
  licenses,
  testId,
}: {
  value: AssignedToFilterValue[];
  onChange: (value: AssignedToFilterValue[]) => void;
  licenses?: keyof Licenses;
  testId?: string;
}): React.ReactElement => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [shouldLoad, setShouldLoad] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const request = React.useCallback(
    (params?: SearchRequest) => API.user.searchUsers({ ...params, licenses }),
    [licenses]
  );

  const { results, loading, loadingMore, hasMore, loadMore, searchQuery } = useInfiniteSearch(request, shouldLoad);

  const open = Boolean(anchorEl);
  const isAllSelected = value.length === 0;

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
    setShouldLoad(true);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
    setQuery('');
    searchQuery('');
  };

  const handleAllClick = (): void => {
    onChange([]);
  };

  const handleOptionClick = (user: UuidableName): void => {
    const isSelected = value.some((v) => v.uuid === user.uuid);
    if (isSelected) {
      onChange(value.filter((v) => v.uuid !== user.uuid));
    } else {
      onChange([...value, user]);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setQuery(event.target.value);
    searchQuery(event.target.value);
  };

  const MAX_VISIBLE_AVATARS = 3;
  const visibleUsers = value.slice(0, MAX_VISIBLE_AVATARS);
  const additionalCount = value.length > MAX_VISIBLE_AVATARS ? value.length - MAX_VISIBLE_AVATARS : 0;

  return (
    <>
      <FilterButton
        prefix="Assigned to"
        value={isAllSelected ? 'All' : undefined}
        onClick={handleOpen}
        isOpen={open}
        testId={testId}
      >
        {value.length > 0 && (
          <AvatarsContainer>
            {visibleUsers.map((user) => (
              <InitialAvatar key={user.uuid} name={user.name} size="small" />
            ))}
            {additionalCount > 0 && <CountAvatar name={`+${additionalCount}`} size="small" disableFormattedName />}
          </AvatarsContainer>
        )}
      </FilterButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <OptionsContainer>
          <SearchContainer>
            <TextField
              size="small"
              placeholder="Search users..."
              value={query}
              onChange={handleSearchChange}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                  </InputAdornment>
                ),
              }}
            />
          </SearchContainer>

          <Divider />

          <OptionsListContainer
            onScroll={(event) => {
              if (hasMore && isNearBottom(event.currentTarget)) {
                loadMore();
              }
            }}
          >
            <OptionRow onClick={handleAllClick}>
              <AllOptionText>All</AllOptionText>
              <Checkbox checked={isAllSelected} size="small" sx={{ padding: 0 }} />
            </OptionRow>

            <Divider />

            {loading && (
              <LoadingContainer>
                <CircularProgress size={24} />
              </LoadingContainer>
            )}

            {!loading && results.length === 0 && <NoResultsText>No users found</NoResultsText>}

            {!loading &&
              results.map((user) => {
                const isSelected = value.some((v) => v.uuid === user.uuid);

                return (
                  <OptionRow key={user.uuid} onClick={() => handleOptionClick(user)}>
                    <UserInfo>
                      <InitialAvatar name={user.name} size="small" />
                      <UserTextColumn>
                        <UserName>
                          <HighlightedText text={user.name} query={query} />
                        </UserName>
                        {user.email && (
                          <UserEmail>
                            <HighlightedText text={user.email} query={query} />
                          </UserEmail>
                        )}
                      </UserTextColumn>
                    </UserInfo>
                    <Checkbox checked={isSelected} size="small" sx={{ padding: 0 }} />
                  </OptionRow>
                );
              })}

            {loadingMore && (
              <LoadingContainer>
                <CircularProgress size={24} />
              </LoadingContainer>
            )}
          </OptionsListContainer>
        </OptionsContainer>
      </Popover>
    </>
  );
};

export default AssignedToPicker;
